import React, { useState, useEffect } from "react";
import Select, { ActionMeta, SingleValue } from "react-select";
import useErrorHandler from "@beeldit/core/errors/useErrorHandler";

import index from "@beeldit/core/services";
import { Customer } from "../models/Customer";
import { FixedSizeList as List } from "react-window";
import { Eye } from "@phosphor-icons/react/dist/ssr";
import useEditAction from "../actions/edit/useEditAction";
import configCustomersAndPets from "../configCustomersAndPets";
import useEditWithModalAction from "../actions/edit/useEditWithModalAction";

interface Option {
  label: string;
  value: number | string;
}

const customMenuList = (props: any) => {
  const { options, children, getValue, setValue, selectProps } = props;
  const height = 35; // altura de cada opción
  const itemCount = options.length;

  // Ajustamos la altura máxima de la lista
  const maxHeight = itemCount * height > 300 ? 300 : itemCount * height;

  return (
    <List
      height={maxHeight} // Altura dinámica según el número de opciones
      itemCount={itemCount}
      itemSize={height}
      width="100%"
    >
      {({ index, style }) => <div style={style}>{children[index]}</div>}
    </List>
  );
};

function CustomerWithModalSelector(props: any) {
  const { id, value, required, disabled, readonly, onChange, formContext } =
    props;
  const [selectedOption, setSelectedOption] = useState<Option | null>(null);
  const [options, setOptions] = useState<Option[]>([]);
  const [elementsData, setElementsData] = useState({ elements: [] });

  const { element, setElement } = formContext;
  const errorHandler = useErrorHandler();

  useEffect(() => {
    index("customers", "select=id,name,surnames,nif")
      .then((response: any) => {
        setElementsData(response.data);
      })
      .catch((error: any) => {
        errorHandler(error, null);
      });
  }, []);

  useEffect(() => {
    const options = elementsData.elements.map((customer: Customer) => {
      const label =
        customer.name + " " + customer.surnames + " - " + customer.nif;
      return { label: label, value: customer.id };
    });
    setOptions(options);
    const currentSelectedOption = options.find(
      (option) => option.value === value
    );
    setSelectedOption(currentSelectedOption || null);
  }, [elementsData, value]);

  const handleOnChange = (
    newValue: SingleValue<Option>,
    actionMeta: ActionMeta<Option>
  ) => {
    const selectedOption = newValue as Option | null;
    setSelectedOption(selectedOption);
    const model = selectedOption?.value ?? null;
    onChange(model);
  };

  const selectStyles = {
    control: (provided: any) => ({
      ...provided,
      borderColor: "#E7F5F2",
      backgroundColor: "#E7F5F2",
      borderRadius: "0.25rem",
      width: "450px",
    }),
  };

  const disabledByreadOnly = () => {
    if (typeof readonly === "function") {
      return readonly(element);
    }
    return readonly;
  };
  const scrollToTop = () => {
    // Obtenemos el selector por el id y obtenemos su su tercer hijo
    const selectElement = document.getElementById(id);
    // Obtenemos el tercer hijo del selector
    const thirdChild = selectElement?.children[3];
    if (thirdChild) {
      // Entramos en el hijo y obtenemos el primer hijo
      const firstChild = thirdChild.children[0];
      firstChild.scrollTop = 0; // Hacemos scroll al principio
    }
  };

  const { getFunction, actionTemplate: editActionTemplate } =
    useEditWithModalAction(configCustomersAndPets);

  return (
    <>
      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <Select
          id={id}
          options={options}
          value={selectedOption}
          required={required}
          isDisabled={disabled || disabledByreadOnly() || (element && element.id)}
          onChange={handleOnChange}
          styles={selectStyles}
          onInputChange={scrollToTop}
          placeholder="Selecciona un cliente"
          isClearable={true}
          components={{ MenuList: customMenuList }} // Personaliza el menú de la lista
        />
        {selectedOption && (
          <Eye
            size={20}
            style={{ cursor: "pointer" }}
            onClick={() => {
              getFunction(selectedOption?.value);
            }}
          ></Eye>
        )}
      </div>
      {editActionTemplate}
    </>
  );
}

export default CustomerWithModalSelector;
